import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../context/AuthContext';
import { useDemo, DEMOTEXT_KORT, DEMOTEXT_LÅNG } from '../demo';

const TEMA = {
  bg: '#14161A',
  bgPanel: '#1B1D22',
  border: '#2A2B2E',
  ink: '#F0EEE8',
  inkSoft: '#9B9A93',
  inkFaint: '#6F6E68',
  accent: '#E0A83E',
  accentHover: '#F0BB56',
  onAccent: '#14161A',
  fel: '#D9695F',
};

function säkerställFonter() {
  if (document.getElementById('vinsales-fonter')) return;
  const preconnect1 = document.createElement('link');
  preconnect1.rel = 'preconnect';
  preconnect1.href = 'https://fonts.googleapis.com';
  const preconnect2 = document.createElement('link');
  preconnect2.rel = 'preconnect';
  preconnect2.href = 'https://fonts.gstatic.com';
  preconnect2.crossOrigin = 'anonymous';
  const stylesheet = document.createElement('link');
  stylesheet.id = 'vinsales-fonter';
  stylesheet.rel = 'stylesheet';
  stylesheet.href = 'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap';
  document.head.append(preconnect1, preconnect2, stylesheet);
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [lösenord, setLösenord] = useState('');
  const [laddar, setLaddar] = useState(false);
  const [fel, setFel] = useState('');
  const [fokusFält, setFokusFält] = useState<'email' | 'lösenord' | 'återställ' | null>(null);
  const [läge, setLäge] = useState<'login' | 'glömt'>('login');
  const [återställEmail, setÅterställEmail] = useState('');
  const [återställStatus, setÅterställStatus] = useState<'idle' | 'skickar' | 'skickat' | 'fel'>('idle');
  const navigate = useNavigate();
  const demo = useDemo();

  useEffect(() => {
    säkerställFonter();
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate('/idag');
    });
  }, [navigate]);

  async function loggaIn(e: React.FormEvent) {
    e.preventDefault();
    setLaddar(true);
    setFel('');
    const { error } = await supabase.auth.signInWithPassword({ email, password: lösenord });
    if (error) {
      // En gäst vars tid i demon har gått ut är spärrad, och ska få veta varför.
      const spärrad = /banned/i.test(String((error as { message?: string }).message || ''));
      setFel(spärrad
        ? (demo ? 'Din demoinloggning har gått ut. Hör av dig till oss så öppnar vi den igen.' : 'Kontot är inaktiverat. Kontakta din admin.')
        : 'Fel e-post eller lösenord');
      setLaddar(false);
    } else {
      navigate('/idag');
    }
  }

  async function skickaÅterställning(e: React.FormEvent) {
    e.preventDefault();
    setÅterställStatus('skickar');
    const { error } = await supabase.auth.resetPasswordForEmail(återställEmail, {
      redirectTo: `${window.location.origin}/aterstall-losenord`,
    });
    setÅterställStatus(error ? 'fel' : 'skickat');
  }

  function tillbakaTillInloggning() {
    setLäge('login');
    setÅterställStatus('idle');
    setÅterställEmail('');
  }

  const inputStyle = (fält: 'email' | 'lösenord' | 'återställ'): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    borderRadius: '2px',
    border: `1px solid ${fokusFält === fält ? TEMA.inkSoft : TEMA.border}`,
    fontSize: '14px',
    fontFamily: "'Inter', sans-serif",
    boxSizing: 'border-box',
    outline: 'none',
    background: TEMA.bg,
    color: TEMA.ink,
    transition: 'border-color 0.15s ease',
  });

  const labelStyle: React.CSSProperties = {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '11px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: TEMA.accent,
    display: 'block',
    marginBottom: '6px',
  };

  return (
    <div style={{ minHeight: '100vh', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: TEMA.bg }}>
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem', width: '100%', maxWidth: '400px', padding: '0 1rem' }}>

        {/* Logotyp — samma lockup som hemsidan: "Vin" i ink, "sales" kursiverat i guld */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontFamily: "'Fraunces', serif", fontSize: '30px', color: TEMA.ink, letterSpacing: '-0.01em' }}>
            Vin<em style={{ fontStyle: 'italic', color: TEMA.accent }}>sales</em>
          </span>
        </div>

        {/* Formulär */}
        <div style={{
          background: TEMA.bgPanel,
          borderRadius: '12px',
          border: `1px solid ${TEMA.border}`,
          padding: '2rem',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          {läge === 'login' ? (
            <>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkSoft, margin: '0 0 1.5rem', textAlign: 'center' }}>
                Logga in för att fortsätta
              </p>

              <form onSubmit={loggaIn}>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>E-post</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onFocus={() => setFokusFält('email')}
                    onBlur={() => setFokusFält(null)}
                    style={inputStyle('email')}
                    placeholder="din@email.com"
                    required
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>Lösenord</label>
                    <button
                      type="button"
                      onClick={() => setLäge('glömt')}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        fontFamily: "'Inter', sans-serif", fontSize: '12px', color: TEMA.inkSoft,
                      }}
                    >
                      Glömt lösenordet?
                    </button>
                  </div>
                  <input
                    type="password"
                    value={lösenord}
                    onChange={e => setLösenord(e.target.value)}
                    onFocus={() => setFokusFält('lösenord')}
                    onBlur={() => setFokusFält(null)}
                    style={inputStyle('lösenord')}
                    placeholder="••••••••"
                    required
                  />
                </div>

                {fel && (
                  <p style={{ fontFamily: "'Inter', sans-serif", color: TEMA.fel, fontSize: '13px', margin: '0 0 1rem', textAlign: 'center' }}>{fel}</p>
                )}

                <button
                  type="submit"
                  disabled={laddar}
                  style={{
                    width: '100%', padding: '11px', borderRadius: '2px',
                    background: TEMA.accent,
                    color: TEMA.onAccent, fontSize: '14px',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 500, border: `1px solid ${TEMA.accent}`, cursor: laddar ? 'default' : 'pointer',
                    transition: 'background 0.15s ease, opacity 0.15s ease',
                    opacity: laddar ? 0.7 : 1,
                  }}
                  onMouseEnter={e => { if (!laddar) e.currentTarget.style.background = TEMA.accentHover; }}
                  onMouseLeave={e => { if (!laddar) e.currentTarget.style.background = TEMA.accent; }}
                >
                  {laddar ? 'Loggar in...' : 'Logga in'}
                </button>
              </form>
            </>
          ) : (
            <>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkSoft, margin: '0 0 1.5rem', textAlign: 'center' }}>
                Återställ ditt lösenord
              </p>

              {återställStatus === 'skickat' ? (
                <>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.ink, textAlign: 'center', margin: '0 0 1.5rem' }}>
                    Om adressen finns hos oss har vi skickat en återställningslänk till <strong>{återställEmail}</strong>.
                  </p>
                  <button
                    type="button"
                    onClick={tillbakaTillInloggning}
                    style={{
                      width: '100%', padding: '11px', borderRadius: '2px',
                      background: 'transparent',
                      color: TEMA.ink, fontSize: '14px',
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500, border: `1px solid ${TEMA.border}`, cursor: 'pointer',
                    }}
                  >
                    Tillbaka till inloggning
                  </button>
                </>
              ) : (
                <form onSubmit={skickaÅterställning}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={labelStyle}>E-post</label>
                    <input
                      type="email"
                      value={återställEmail}
                      onChange={e => setÅterställEmail(e.target.value)}
                      onFocus={() => setFokusFält('återställ')}
                      onBlur={() => setFokusFält(null)}
                      style={inputStyle('återställ')}
                      placeholder="din@email.com"
                      required
                    />
                  </div>

                  {återställStatus === 'fel' && (
                    <p style={{ fontFamily: "'Inter', sans-serif", color: TEMA.fel, fontSize: '13px', margin: '0 0 1rem', textAlign: 'center' }}>
                      Något gick fel. Försök igen om en stund.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={återställStatus === 'skickar'}
                    style={{
                      width: '100%', padding: '11px', borderRadius: '2px',
                      background: TEMA.accent,
                      color: TEMA.onAccent, fontSize: '14px',
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500, border: `1px solid ${TEMA.accent}`, cursor: återställStatus === 'skickar' ? 'default' : 'pointer',
                      transition: 'background 0.15s ease, opacity 0.15s ease',
                      opacity: återställStatus === 'skickar' ? 0.7 : 1,
                      marginBottom: '12px',
                    }}
                    onMouseEnter={e => { if (återställStatus !== 'skickar') e.currentTarget.style.background = TEMA.accentHover; }}
                    onMouseLeave={e => { if (återställStatus !== 'skickar') e.currentTarget.style.background = TEMA.accent; }}
                  >
                    {återställStatus === 'skickar' ? 'Skickar...' : 'Skicka återställningslänk'}
                  </button>

                  <button
                    type="button"
                    onClick={tillbakaTillInloggning}
                    style={{
                      width: '100%', background: 'none', border: 'none', padding: '4px',
                      fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkSoft, cursor: 'pointer',
                    }}
                  >
                    Tillbaka till inloggning
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        {demo && (
          <div style={{ border: `1px solid ${TEMA.accent}`, borderRadius: '8px', padding: '14px 16px', width: '100%', boxSizing: 'border-box' }}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 500, color: TEMA.ink, margin: '0 0 6px', lineHeight: 1.5 }}>
              {DEMOTEXT_KORT}
            </p>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12.5px', color: TEMA.inkSoft, margin: 0, lineHeight: 1.5 }}>
              {DEMOTEXT_LÅNG}
            </p>
          </div>
        )}

        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkSoft, textAlign: 'center', margin: 0 }}>
          Om du inte har ett konto ännu, vänligen{' '}
          <a href="mailto:hej@vinsales.se" style={{ color: TEMA.accent, textDecoration: 'none' }}>
            kontakta oss
          </a>.
        </p>
      </div>
    </div>
  );
}